/**
 * 管理端 API 客户端。
 *
 * 所有请求经 Vite 代理转到 Fastify（开发期 `/api` → 127.0.0.1:8787）。
 * 令牌存于 localStorage —— 管理端是内网运维工具，若需更高保证应改为
 * httpOnly Cookie + CSRF 令牌（见 docs 安全模型）。
 */
import { useCallback, useEffect, useRef, useState } from 'react'

const TOKEN_KEY = 'dskgi-admin-token'

/** 令牌失效（401）时广播，供 App 卸载受保护界面并跳回登录页。 */
export const SESSION_EXPIRED_EVENT = 'dskgi:session-expired'

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

export function setToken(token: string | null): void {
  if (token) localStorage.setItem(TOKEN_KEY, token)
  else localStorage.removeItem(TOKEN_KEY)
}

/** 由 App 订阅；返回取消订阅函数。 */
export function onSessionExpired(handler: () => void): () => void {
  window.addEventListener(SESSION_EXPIRED_EVENT, handler)
  return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handler)
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

async function request<T>(
  path: string,
  opts: { method?: string; body?: unknown } = {},
): Promise<T> {
  const token = getToken()
  const res = await fetch(`/api${path}`, {
    method: opts.method ?? 'GET',
    headers: {
      ...(opts.body !== undefined ? { 'content-type': 'application/json' } : {}),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    ...(opts.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
  })

  const text = await res.text()
  // 两个分支都会赋值，故不写初始值（写了也是死赋值，ESLint 的 no-useless-assignment 会报）
  let json: unknown
  try {
    json = text ? JSON.parse(text) : null
  } catch {
    json = null
  }

  if (!res.ok) {
    const msg =
      json && typeof json === 'object' && 'error' in json
        ? String((json as { error: unknown }).error)
        : `请求失败 (${res.status})`
    if (res.status === 401) {
      setToken(null)
      // 通知 App 卸载受保护界面并跳回登录页，否则用户只会看到一堆 401 提示
      window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT))
    }
    throw new ApiError(msg, res.status)
  }
  return json as T
}

/**
 * 从 `content-disposition` 里取出文件名。
 *
 * 服务端只会发纯 ASCII 的 `filename="…"`（不用 `filename*`），所以这里不做 UTF-8 解码。
 * 解析不出来时回退一个带本地时间戳的名字 —— 有名字总比浏览器默认的 "download" 强。
 */
function filenameFrom(header: string | null, fallbackPrefix: string): string {
  const m = /filename="?([^";]+)"?/i.exec(header ?? '')
  if (m?.[1]) return m[1]
  const d = new Date()
  const p = (n: number): string => String(n).padStart(2, '0')
  return `${fallbackPrefix}-${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}.csv`
}

export interface CsvDownload {
  blob: Blob
  filename: string
  /** 服务端声明的数据行数；老接口没有这个头时为 null。 */
  rows: number | null
}

/**
 * 下载任意 CSV 导出接口。
 *
 * 为什么不复用 `request<T>()`：那个函数结构上只会 `res.text()` → `JSON.parse`，
 * 拿不到二进制。给它加一个 mode 参数会让唯一的请求路径长出两条分支，比另开一个函数更难读。
 *
 * ⚠️ 401 必须与 `request()` 走**同一套**处理（清令牌 + 广播过期事件），否则令牌过期时
 * 用户只会看到「导出失败」，却不会被带回登录页。三个导出接口共用这里，就不会各写一遍。
 *
 * 返回 Blob 而不是在这里直接触发下载：下载属于 UI 行为，留在页面里更好测也好读。
 */
export async function downloadCsv(
  path: string,
  params: Record<string, string | undefined> = {},
  opts: { fallbackPrefix?: string } = {},
): Promise<CsvDownload> {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) if (v) p.set(k, v)
  const qs = p.toString()

  const token = getToken()
  const res = await fetch(`/api${path}${qs ? `?${qs}` : ''}`, {
    headers: token ? { authorization: `Bearer ${token}` } : {},
  })

  if (!res.ok) {
    // 失败时响应体是 JSON（与其它接口一致），据此还原原因
    const text = await res.text()
    // 两个分支都会赋值，故不写初始值（与 request() 同样处理，见其注释）
    let json: unknown
    try {
      json = text ? JSON.parse(text) : null
    } catch {
      json = null
    }
    if (res.status === 401) {
      setToken(null)
      window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT))
    }
    const msg =
      json && typeof json === 'object' && 'error' in json
        ? String((json as { error: unknown }).error)
        : `导出失败 (${res.status})`
    throw new ApiError(msg, res.status)
  }

  const rawRows = res.headers.get('x-export-rows')
  const rows = rawRows === null ? null : Number(rawRows)

  return {
    blob: await res.blob(),
    filename: filenameFrom(res.headers.get('content-disposition'), opts.fallbackPrefix ?? 'export'),
    rows: rows !== null && Number.isFinite(rows) ? rows : null,
  }
}

/**
 * 导出操作日志 CSV。
 *
 * 行数用服务端给的，不用列表接口的 total：导出会先写一条自己的 `audit.export`
 * 记录，那条也在文件里，用 total 会少报 1 条。
 */
export function downloadAuditCsv(
  filters: { action?: string; target?: string } = {},
): Promise<CsvDownload> {
  return downloadCsv('/audit/export', filters, { fallbackPrefix: 'audit' })
}

/** 导出设备清单 CSV（筛选条件与设备列表页一致）。 */
export function downloadDevicesCsv(
  filters: { status?: string; keyword?: string } = {},
): Promise<CsvDownload> {
  return downloadCsv('/devices/export', filters, { fallbackPrefix: 'devices' })
}

/** 导出授权码台账 CSV。 */
export function downloadLicensesCsv(
  filters: { deviceId?: string; keyword?: string } = {},
): Promise<CsvDownload> {
  return downloadCsv('/licenses/export', filters, { fallbackPrefix: 'licenses' })
}

// ───────────────────────────── 类型 ─────────────────────────────

export type DeviceStatus = 'pending' | 'active' | 'suspended' | 'retired'

export interface Device {
  id: string
  device_id: string
  device_name: string
  device_type: string
  model: string | null
  serial_number: string | null
  android_id: string | null
  app_version: string | null
  machine_code: string | null
  public_key_pem: string | null
  status: DeviceStatus
  stage_lock_enabled: number
  registered_at: number
  approved_at: number | null
  approved_by: string | null
  last_seen_at: number | null
  note: string | null
  has_secret?: number
  // ── 设备 App 注册表单（仪器上填写并上报）──
  company_name: string | null
  contact_name: string | null
  contact_phone: string | null
  department: string | null
  form_reported_at: number | null
}

export interface DeviceDetail {
  device: Device
  hasSecret: boolean
  stageState: {
    device_id: string
    elapsed_ms: number
    fully_activated: number
    stage1_activated: number
    stage2_activated: number
    stage3_activated: number
    reported_at: number
  } | null
}

export interface DynamicPassword {
  deviceId: string
  current: string
  next: string
  secondsRemaining: number
  window: number
  stepSeconds: number
  generatedAt: number
}

export interface ActivationIssue {
  deviceId: string
  stage: 1 | 2 | 3
  code: string
  window: number
  validForSeconds: number
  issuedAt: number
}

export interface ActivationCodeRow {
  id: string
  stage: number
  code: string
  issued_at: number
  issued_by: string | null
  totp_window: number
  verified_at: number | null
  note: string | null
}

export interface LicenseCodeRow {
  id: string
  device_id: string
  machine_code: string
  license_key: string
  issued_at: number
  issued_by: string | null
  note: string | null
}

export interface AuditRow {
  id: string
  actor: string | null
  action: string
  target: string | null
  detail: string | null
  /** 变更前的值（docs/11 要求「修改前/修改后」）。旧记录为 null。 */
  old_value: string | null
  new_value: string | null
  ip: string | null
  created_at: number
}

/**
 * 管理端能力清单（与 server/src/authz.ts 的 Capability 对应）。
 *
 * 角色与矩阵存库后，前端不再维护「角色 → 能力」的副本；这个联合类型只用于
 * `can('device:read')` 这类调用的编译期校验。实际能做什么以接口返回的
 * `SessionUser.capabilities` 为准。
 */
export type Capability =
  | 'device:read'
  | 'device:write'
  | 'device:delete'
  | 'secret:issue'
  | 'secret:reveal'
  | 'secret:revoke'
  | 'provision:build'
  | 'license:issue'
  | 'license:read'
  | 'dynamic-password:read'
  | 'activation:issue'
  | 'activation:check'
  | 'activation:read'
  | 'signature:create'
  | 'signature:read'
  | 'audit:read'
  | 'user:read'
  | 'user:write'
  | 'role:read'
  | 'role:write'

/** 角色标识（由角色管理页维护，内置角色为 admin / engineer / operator）。 */
export type Role = string

/** 全局授权码台账的一行（比设备详情页多带设备名）。 */
export interface LicenseRecord {
  id: string
  device_id: string
  device_name: string
  machine_code: string
  license_key: string
  issued_at: number
  issued_by: string | null
  note: string | null
}

/** 账号管理列表行（不含密码哈希）。 */
export interface UserRow {
  id: string
  username: string
  role: string
  created_at: number
  last_login_at: number | null
  is_active: number
  must_change_password: number
}

export interface SessionUser {
  id: string
  username: string
  role: string
  /** 角色展示名。服务端按当前角色返回；旧会话可能缺失。 */
  roleName?: string
  /** 当前角色拥有的能力，登录与 /auth/me 返回。 */
  capabilities?: Capability[]
  /** 管理员代建 / 重置密码后为 true：必须先改成自己的密码才能用其它接口。 */
  mustChangePassword?: boolean
}

/** 角色列表行。 */
export interface RoleRow {
  id: string
  name: string
  description: string | null
  builtin: number
  created_at: number
  updated_at: number
  user_count: number
  capabilities: Capability[]
}

export interface CapabilityMetaItem {
  capability: Capability
  label: string
  hint?: string
}

export interface CapabilityGroup {
  key: string
  label: string
  /** 界面不展示的组（对应功能已隐藏）。 */
  hidden?: boolean
  items: CapabilityMetaItem[]
}

export interface RoleListResponse {
  rows: RoleRow[]
  meta: CapabilityGroup[]
}

export interface SignatureRow {
  id: string
  user_id: string
  username: string
  role: string
  action: string
  record_type: string
  record_id: string
  hash_value: string
  signature: string
  signature_algorithm: string
  payload: string | null
  signed_at: number
  ip: string | null
}

export interface SignatureVerifyResult {
  valid: boolean
  hashStored: string
  hashRecomputed?: string
  signatureValid: boolean
  reason?: string
}

export interface DevicePatch {
  deviceName?: string
  deviceType?: string
  model?: string
  serialNumber?: string
  androidId?: string
  appVersion?: string
  machineCode?: string
  publicKeyPem?: string
  note?: string
  stageLockEnabled?: boolean
  // 注册表单：运维可修正现场填错的内容
  companyName?: string
  contactName?: string
  contactPhone?: string
  department?: string
}

export interface Paged<T> {
  rows: T[]
  total: number
  page: number
  pageSize: number
}

export interface Health {
  ok: boolean
  masterKeyConfigured: boolean
  /** 签发私钥是否就绪（未就绪时授权码签发接口返回 503）。 */
  licenseKeyConfigured?: boolean
  licenseKeyError?: string
  /** 电子签名私钥是否就绪（未就绪时签名接口返回 503）。 */
  signatureKeyConfigured?: boolean
  signatureKeyError?: string
  /** 是否存在至少一个 admin 角色账号。 */
  hasAdmin?: boolean
  time: number
}

// ───────────────────────────── 接口 ─────────────────────────────

export const api = {
  health: () => request<Health>('/health'),

  login: (username: string, password: string) =>
    request<{ token: string; user: SessionUser }>('/auth/login', {
      method: 'POST',
      body: { username, password },
    }),

  me: () => request<{ user: SessionUser }>('/auth/me'),

  // ── 账号管理（仅 admin） ──

  listUsers: () => request<{ rows: UserRow[] }>('/users'),

  createUser: (body: { username: string; password: string; role: Role }) =>
    request<UserRow>('/users', { method: 'POST', body }),

  /** 改角色 / 启停。只传要改的那个字段即可。 */
  updateUser: (id: string, body: { role?: Role; active?: boolean }) =>
    request<UserRow>(`/users/${id}`, { method: 'PATCH', body }),

  resetUserPassword: (id: string, password: string) =>
    request<UserRow>(`/users/${id}/password`, { method: 'POST', body: { password } }),

  deleteUser: (id: string) => request<{ ok: true }>(`/users/${id}`, { method: 'DELETE' }),

  // ── 角色与权限（角色页 / 用户页的角色下拉） ──

  listRoles: () => request<RoleListResponse>('/roles'),

  createRole: (body: {
    id: string
    name: string
    description?: string | null
    capabilities?: Capability[]
  }) => request<RoleRow>('/roles', { method: 'POST', body }),

  updateRole: (
    id: string,
    body: { name?: string; description?: string | null; capabilities?: Capability[] },
  ) => request<RoleRow>(`/roles/${encodeURIComponent(id)}`, { method: 'PATCH', body }),

  deleteRole: (id: string) =>
    request<{ ok: true }>(`/roles/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  /** 修改自己的密码。 */
  /**
   * 自助改密。服务端会自增令牌版本并**补发一个新令牌** —— 旧令牌在改密瞬间失效，
   * 不换的话用户改完就被踢回登录页（强制改密流程里尤其难受）。
   */
  changePassword: (oldPassword: string, newPassword: string) =>
    request<{ ok: true; token: string }>('/auth/password', {
      method: 'POST',
      body: { oldPassword, newPassword },
    }),

  // ── 电子签名 ──

  listSignatures: (
    q: { page?: number; pageSize?: number; recordType?: string; recordId?: string } = {},
  ) => {
    const p = new URLSearchParams()
    for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== '') p.set(k, String(v))
    return request<Paged<SignatureRow>>(`/signatures?${p.toString()}`)
  },

  createSignature: (body: {
    action?: string
    recordType: string
    recordId: string
    payload: unknown
  }) => request<SignatureRow>('/signatures', { method: 'POST', body }),

  verifySignature: (id: string) =>
    request<SignatureVerifyResult>(`/signatures/${encodeURIComponent(id)}/verify`),

  listDevices: (
    q: { page?: number; pageSize?: number; status?: string; keyword?: string } = {},
  ) => {
    const p = new URLSearchParams()
    for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== '') p.set(k, String(v))
    return request<Paged<Device>>(`/devices?${p.toString()}`)
  },

  getDevice: (deviceId: string) =>
    request<DeviceDetail>(`/devices/${encodeURIComponent(deviceId)}`),

  registerDevice: (body: {
    deviceId: string
    deviceName: string
    deviceType: string
    model?: string
    serialNumber?: string
    androidId?: string
    appVersion?: string
    machineCode?: string
    publicKeyPem?: string
    note?: string
    companyName?: string
    contactName?: string
    contactPhone?: string
    department?: string
  }) => request<Device>('/devices', { method: 'POST', body }),

  /**
   * 更新设备信息。
   *
   * ⚠️ 参数用 **camelCase**：服务端的 `updateDevice` 只读取 camelCase 键
   * （`deviceName` / `deviceType` / … / `stageLockEnabled`）。
   * 传入 snake_case 的 `Device` 字段会通过类型检查但被服务端静默忽略，
   * 故此处刻意只接受 camelCase 补丁对象。
   */
  updateDevice: (deviceId: string, patch: DevicePatch) =>
    request<{ ok: true }>(`/devices/${encodeURIComponent(deviceId)}`, {
      method: 'PATCH',
      body: patch,
    }),

  /**
   * 删除设备（级联删除设备密钥、仪器激活码、上位机授权码、阶段锁状态）。
   * **不可逆**，调用前务必二次确认。
   */
  deleteDevice: (deviceId: string) =>
    request<{ deviceId: string; removed: Record<string, number> }>(
      `/devices/${encodeURIComponent(deviceId)}`,
      { method: 'DELETE' },
    ),

  setStatus: (deviceId: string, status: DeviceStatus) =>
    request<{ ok: true }>(`/devices/${encodeURIComponent(deviceId)}/status`, {
      method: 'POST',
      body: { status },
    }),

  issueSecret: (deviceId: string) =>
    request<{ secretBase32: string; keyVersion: number }>(
      `/devices/${encodeURIComponent(deviceId)}/secret`,
      {
        method: 'POST',
      },
    ),

  revealSecret: (deviceId: string) =>
    request<{ secretBase32: string; keyVersion: number }>(
      `/devices/${encodeURIComponent(deviceId)}/secret`,
    ),

  revokeSecret: (deviceId: string) =>
    request<{ ok: true }>(`/devices/${encodeURIComponent(deviceId)}/secret`, { method: 'DELETE' }),

  provision: (deviceId: string) =>
    request<{ envelope: string; keyVersion: number }>(
      `/devices/${encodeURIComponent(deviceId)}/provision`,
      {
        method: 'POST',
      },
    ),

  /**
   * 签发上位机授权码（RSA-2048 对**上位机机器码**签名）。
   * 需设备已登记 machineCode；未登记时返回 409。
   */
  issueLicense: (deviceId: string, body: { machineCode?: string; note?: string } = {}) =>
    request<LicenseCodeRow>(`/devices/${encodeURIComponent(deviceId)}/license`, {
      method: 'POST',
      body,
    }),

  listLicenseCodes: (deviceId: string) =>
    request<{ rows: LicenseCodeRow[] }>(`/devices/${encodeURIComponent(deviceId)}/license`),

  dynamicPassword: (deviceId: string) =>
    request<DynamicPassword>(`/devices/${encodeURIComponent(deviceId)}/dynamic-password`),

  issueActivation: (deviceId: string, stage: 1 | 2 | 3, note?: string) =>
    request<ActivationIssue>(`/devices/${encodeURIComponent(deviceId)}/activation`, {
      method: 'POST',
      body: { stage, ...(note ? { note } : {}) },
    }),

  checkActivation: (deviceId: string, stage: 1 | 2 | 3, code: string) =>
    request<{ valid: boolean; driftWindows: number | null }>(
      `/devices/${encodeURIComponent(deviceId)}/activation/check`,
      { method: 'POST', body: { stage, code } },
    ),

  listActivationCodes: (deviceId: string) =>
    request<{ rows: ActivationCodeRow[] }>(`/devices/${encodeURIComponent(deviceId)}/activation`),

  listLicenses: (
    q: { page?: number; pageSize?: number; deviceId?: string; keyword?: string } = {},
  ) => {
    const p = new URLSearchParams()
    for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== '') p.set(k, String(v))
    return request<Paged<LicenseRecord>>(`/licenses?${p.toString()}`)
  },

  listAudit: (q: { page?: number; pageSize?: number; action?: string; target?: string } = {}) => {
    const p = new URLSearchParams()
    for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== '') p.set(k, String(v))
    return request<Paged<AuditRow>>(`/audit?${p.toString()}`)
  },
}

// ───────────────────────── 简易数据钩子 ─────────────────────────

export interface AsyncState<T> {
  data: T | null
  error: string | null
  loading: boolean
  reload: () => void
}

/** 轻量数据钩子，避免为管理端引入完整查询库。 */
export function useAsync<T>(fn: () => Promise<T>, deps: unknown[] = []): AsyncState<T> {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [nonce, setNonce] = useState(0)
  const fnRef = useRef(fn)
  fnRef.current = fn

  useEffect(() => {
    let alive = true
    setLoading(true)
    setError(null)
    fnRef
      .current()
      .then((d) => {
        if (alive) setData(d)
      })
      .catch((e: unknown) => {
        if (alive) setError(e instanceof Error ? e.message : String(e))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce])

  const reload = useCallback(() => setNonce((n) => n + 1), [])
  return { data, error, loading, reload }
}

/** 每秒刷新的时钟，用于维护动态密码倒计时。 */
export function useTicker(intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs)
    return () => clearInterval(id)
  }, [intervalMs])
  return now
}
